import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import EventCard from '@/components/feature/EventCard';
import Pagination from '@/components/feature/Pagination';
import FilterBar from './components/FilterBar';
import usePageMeta from '@/hooks/usePageMeta';
import { filterEvents, getCategoryById, paginate, totalPages } from '@/utils/news';

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const { categoryId = 'all' } = useParams();
  const category = getCategoryById(categoryId);
  const isAll = categoryId === 'all';
  const name = isAll ? '全部赛事' : category?.name ?? '赛事资讯';
  const desc = isAll
    ? '汇聚全网网红大赛的最新资讯与赛事动态，支持按类型、地区、时间多维筛选。'
    : category?.desc ?? '该类赛事的最新资讯与精彩回顾。';

  usePageMeta({
    title: `${name} - 每日大瓜网红大赛百度`,
    description: `${name}专栏，涵盖网红大赛最新赛事资讯、赛事动态与精彩回顾，按类型、地区、时间多维筛选，助你第一时间掌握热点。`,
    keywords: `${name},网红大赛,网红赛事,赛事资讯,每日大瓜`,
  });

  const [type, setType] = useState(categoryId);
  const [region, setRegion] = useState('');
  const [timeRange, setTimeRange] = useState('');
  const [sort, setSort] = useState('latest');
  const [page, setPage] = useState(1);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setType(categoryId);
    setPage(1);
  }, [categoryId]);

  const filtered = useMemo(
    () => filterEvents({ categoryId: type, region, timeRange, sort }),
    [type, region, timeRange, sort],
  );

  const pages = totalPages(filtered.length, PAGE_SIZE);
  const pageItems = paginate(filtered, page, PAGE_SIZE);

  const handleReset = () => {
    setType(categoryId);
    setRegion('');
    setTimeRange('');
    setSort('latest');
    setPage(1);
  };

  const handlePageChange = (next: number) => {
    setPage(next);
    if (listRef.current) {
      listRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <SiteLayout>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
        <Breadcrumb items={[{ label: name }]} />

        <header className="mt-4 mb-6 md:mb-8">
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">{name}</h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-3xl">{desc}</p>
        </header>

        <FilterBar
          type={type}
          region={region}
          timeRange={timeRange}
          sort={sort}
          onTypeChange={(v) => {
            setType(v);
            setPage(1);
          }}
          onRegionChange={(v) => {
            setRegion(v);
            setPage(1);
          }}
          onTimeChange={(v) => {
            setTimeRange(v);
            setPage(1);
          }}
          onSortChange={(v) => {
            setSort(v);
            setPage(1);
          }}
        />

        <div ref={listRef} className="mt-6 md:mt-8 scroll-mt-20">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-foreground-500">
              共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 条赛事资讯
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {pageItems.map((e, i) => (
                  <EventCard key={e.id} event={e} index={i} />
                ))}
              </div>
              <Pagination current={page} total={pages} onChange={handlePageChange} />
            </>
          ) : (
            <div className="rounded-lg border border-background-200 bg-background-50 py-16 px-6 text-center">
              <span className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-3xl">
                <i className="ri-emotion-sad-line"></i>
              </span>
              <h3 className="mt-4 font-heading font-bold text-lg text-foreground-950">
                暂无相关赛事，您可尝试调整筛选条件
              </h3>
              <p className="mt-2 text-sm text-foreground-500">换个地区或时间范围，也许会有惊喜发现。</p>
              <button
                type="button"
                onClick={handleReset}
                className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}